import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Users, Mail, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvitationRegisterPage({ login }) {
    const navigate = useNavigate();
    const { invitationToken } = useParams();
    
    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState(null);
    const [error, setError] = useState(null);
    
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [registering, setRegistering] = useState(false);

    useEffect(() => {
        fetchInvitationDetails();
    }, [invitationToken]);

    const fetchInvitationDetails = async () => {
        try {
            const response = await axios.get(`${API}/api/auth/invitation/${invitationToken}`);
            setInvitation(response.data);
        } catch (error) {
            setError(error.response?.data?.detail || 'Invalid or expired invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error('Please enter your name');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setRegistering(true);
        try {
            const response = await axios.post(
                `${API}/api/auth/register/invitation/${invitationToken}`,
                { name, password }
            );

            toast.success(response.data.message || 'Successfully joined the team!');
            
            // Login the user
            login(response.data.token, response.data.user);
            
            // Navigate to dashboard
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setRegistering(false);
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-100 text-purple-700';
            case 'editor': return 'bg-blue-100 text-blue-700';
            case 'billing': return 'bg-green-100 text-green-700';
            case 'viewer': return 'bg-gray-100 text-gray-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading invitation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <Link to="/" className="text-indigo-600 hover:text-indigo-700 font-medium">
                        Go to homepage
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                {/* Invitation Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Join {invitation.team_name}
                    </h1>
                    <p className="text-gray-600">
                        You've been invited by <strong>{invitation.inviter_email}</strong> to join their team.
                    </p>
                </div>

                {/* Registration Form */}
                <div className="bg-white rounded-xl shadow-lg p-8">
                    {/* Invitation Details */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-3 mb-3">
                            <Mail className="w-5 h-5 text-gray-400" />
                            <div>
                                <div className="text-xs text-gray-500">Your email</div>
                                <div className="font-medium text-gray-900">{invitation.email}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-gray-400" />
                            <div>
                                <div className="text-xs text-gray-500">Your role</div>
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${getRoleColor(invitation.role)}`}>
                                    {invitation.role}
                                </span>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleRegister}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Your Name
                                </label>
                                <Input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter your full name"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Create Password
                                </label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm Password
                                </label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    required
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={registering}
                            className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700"
                        >
                            {registering ? 'Creating account...' : 'Join the Team'}
                        </Button>
                    </form>

                    <p className="mt-6 text-center text-sm text-gray-500">
                        Already have an account?{' '}
                        <Link to="/" className="text-indigo-600 hover:text-indigo-700 font-medium">
                            Sign in
                        </Link>
                    </p>
                </div>

                {/* Info */}
                <p className="mt-4 text-center text-xs text-gray-400">
                    By joining, you agree to our Terms of Service and Privacy Policy.
                </p>
            </div>
        </div>
    );
}
